"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoundScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const ui_1 = require("@pixi/ui");
const gsap_1 = require("gsap");
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
const interfaces_1 = require("../../../shared/interfaces");
const NameBanner_1 = require("../blocks/NameBanner");
const levels_1 = require("../../../shared/levels");
class RoundScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        var _a, _b, _c, _d;
        super(app, sceneManager, gameManager, (0, Global_1.colorForGameType)((_a = gameManager.roomStream.round$.value) === null || _a === void 0 ? void 0 : _a.type));
        this.frametext = null;
        this.round = this.gameManager.roomStream.round$.value;
        // Just an extra safety check
        if (!this.round) {
            this.sceneManager.routeTo(SceneManager_1.Scenes.Home, null, true).then();
            return;
        }
        this.info = this.makeInfo();
        this.wait = this.makeWait();
        this.footerText = this.makeFooterText('typeinfo.' + ((_b = this.round) === null || _b === void 0 ? void 0 : _b.type));
        if (((_c = this.round) === null || _c === void 0 ? void 0 : _c.label) && ((_d = this.round) === null || _d === void 0 ? void 0 : _d.type) === interfaces_1.GameType.Levels) {
            const level = levels_1.levels[(0, levels_1.lvlForTerm)(this.round.label) || 0];
            const txt = i18next_1.default.t('levels.level', { value: level.level });
            this.nameBanner = this.addChild(new NameBanner_1.NameBanner(app, txt, level, this.overSmallSize ? 115 : 100));
            this.nameBanner.bottomlayout = false;
        }
    }
    makeInfo() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const container = new pixi_js_1.Container();
        const hstyle = blocks_1.Block.textStyle(this.overSmallSize ? 52 : 42);
        hstyle.fill = settings_1.Settings.grayColor;
        const ss = (0, Global_1.i18nextss)();
        let text;
        if (((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) === interfaces_1.GameType.Guess || ((_b = this.round) === null || _b === void 0 ? void 0 : _b.type) === interfaces_1.GameType.GuessFriends) {
            text = i18next_1.default.t('guess');
        }
        else {
            const article = i18next_1.default.t('categories.' + ((_c = this.round) === null || _c === void 0 ? void 0 : _c.label) + '.a', {
                defaultValue: ''
            });
            const a = (ss === settings_1.SentenceStructure.VAO && article ? ' ' + article : '');
            text = i18next_1.default.t('draw') + a;
            this.frametext = text;
        }
        const htext = new WigglyText_1.WigglyText(this.app, text, hstyle);
        htext.power = (0, Global_1.px)(7);
        htext.anchor.set(0.5, 0.5);
        const bstyle = blocks_1.Block.textStyle(this.overSmallSize ? 64 : 52);
        bstyle.padding = (0, Global_1.px)(10);
        bstyle.fill = settings_1.Settings.blackColor;
        bstyle.wordWrap = true;
        bstyle.wordWrapWidth = this.app.screen.width;
        let subtext;
        if (((_d = this.round) === null || _d === void 0 ? void 0 : _d.type) === interfaces_1.GameType.Guess || ((_e = this.round) === null || _e === void 0 ? void 0 : _e.type) === interfaces_1.GameType.GuessFriends) {
            subtext = i18next_1.default.t('round', { value: (_f = this.round) === null || _f === void 0 ? void 0 : _f.state });
        }
        else {
            subtext = i18next_1.default.t('categories.' + ((_g = this.round) === null || _g === void 0 ? void 0 : _g.label) + '.n');
            const article = i18next_1.default.t('categories.' + ((_h = this.round) === null || _h === void 0 ? void 0 : _h.label) + '.a', {
                defaultValue: ''
            });
            if (ss === settings_1.SentenceStructure.OAV) {
                subtext = subtext + (article ? ' ' + article : '');
            }
            else if (ss === settings_1.SentenceStructure.AOV) {
                subtext = (article ? article + ' ' : '') + subtext;
            }
            if (ss === settings_1.SentenceStructure.AOV || ss === settings_1.SentenceStructure.OAV) {
                this.frametext = subtext + ' ' + this.frametext;
            }
            else {
                this.frametext += ' ' + subtext;
            }
        }
        const btext = new pixi_js_1.Text(subtext, bstyle);
        btext.anchor.set(0.5, 0.5);
        if (ss === settings_1.SentenceStructure.AOV || ss === settings_1.SentenceStructure.OAV) {
            htext.position.y = btext.height + (0, Global_1.px)(20);
            container.addChild(btext, htext);
        }
        else {
            btext.position.y = htext.height + (0, Global_1.px)(20);
            container.addChild(htext, btext);
        }
        return this.addChild(container);
    }
    makeWait() {
        var _a;
        const wait = new blocks_1.FillingButton(this.app, 150, 60, settings_1.Settings.whiteColor, settings_1.Settings.lightGrayColor);
        wait.scale.set(this.overSmallSize ? 1 : 0.8); // Make it a bit smaller for tiny screens
        const style = blocks_1.Block.textStyle(72);
        style.padding = (0, Global_1.px)(16);
        const counter = new WigglyText_1.WigglyText(this.app, ' ', style);
        counter.scale.set(0.3);
        counter.anchor.set(0.5, 0.5);
        counter.position.y = (0, Global_1.px)(4);
        wait.addChild(counter);
        try {
            const duration = ((_a = this.round) === null || _a === void 0 ? void 0 : _a.start) / 1000;
            const tmbar = wait.timeline(duration);
            for (let i = duration; i > 0; i -= 1) {
                tmbar.to(counter, {
                    pixi: {
                        scale: 1.0
                    },
                    ease: 'power2.out',
                    duration: 1,
                    onStart: () => __awaiter(this, void 0, void 0, function* () {
                        counter.text = Math.ceil(i);
                    }),
                    onComplete: () => {
                        counter.scale.set(0.3);
                    }
                }, i == duration ? 's' : '>');
            }
            this.promisedTimeline(tmbar).then(() => {
                counter.scale.set(1.0);
                counter.text = i18next_1.default.t('go');
            });
        }
        catch (e) {
            console.log(e);
            // Stupid gsap throwing errors sometimes for some reason!
        }
        return this.addChild(wait);
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer) {
                yield this.animateMask(target);
            }
            else {
                // Slide in
                this.position.x = (0, Global_1.isrtl)() ? -this.app.screen.width : this.app.screen.width;
                yield this.promisedTimeline(gsap_1.gsap.timeline()
                    .to(this, {
                    pixi: {
                        positionX: 0
                    },
                    ease: 'power1.out',
                    duration: 0.4
                }));
            }
            return _super.enterScene.call(this, from);
        });
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.init.call(this);
            if (this.gameManager.playerManager.options$.value.sound) {
                (0, Global_1.startTalk)(this.frametext);
            }
        });
    }
    deinit() {
        const _super = Object.create(null, {
            deinit: { get: () => super.deinit }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.deinit.call(this);
            (0, Global_1.stopTalk)();
        });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            this.zIndex = SceneManager_1.Layer.Top;
            const mybutton = (_b = to.getChildByName('game' + ((_a = this.round) === null || _a === void 0 ? void 0 : _a.type), true)) !== null && _b !== void 0 ? _b : to.getChildByName('others', true);
            if (!this.crash && mybutton instanceof ui_1.ButtonContainer) {
                yield this.animateMask(mybutton, true);
            }
            else if (this.crash || ((to === null || to === void 0 ? void 0 : to.name) === SceneManager_1.Scenes.Home && ((_c = this.round) === null || _c === void 0 ? void 0 : _c.type) === interfaces_1.GameType.Tutorial) || ((to === null || to === void 0 ? void 0 : to.name) === SceneManager_1.Scenes.Levels)) {
                yield this.animateFall();
            }
            else {
                // Slide out
                yield this.promisedTimeline(gsap_1.gsap.timeline()
                    .to(this, {
                    pixi: {
                        positionX: (0, Global_1.isrtl)() ? this.app.screen.width : -this.app.screen.width
                    },
                    ease: 'power1.in',
                    duration: 0.4
                }));
            }
            return _super.exitScene.call(this, to);
        });
    }
    backTo() {
        return (0, Global_1.defaultBackToForType)(this.round.type);
    }
    layout() {
        var _a, _b, _c, _d, _e;
        super.layout();
        if (this.info) {
            this.info.children.forEach((text, i) => {
                text.style.fontSize = (0, Global_1.fontSize)(i ? (this.overSmallSize ? 64 : 52) : (this.overSmallSize ? 52 : 42));
            });
        }
        (_a = this.info) === null || _a === void 0 ? void 0 : _a.position.set(this.center.x, this.isLandscapeBox ? this.app.screen.height / 4 : this.app.screen.height / 3);
        (_b = this.nameBanner) === null || _b === void 0 ? void 0 : _b.position.set(this.center.x, this.isLandscapeBox ? (0, Global_1.px)(28) : (0, Global_1.px)(100));
        (_c = this.wait) === null || _c === void 0 ? void 0 : _c.scale.set(this.overSmallSize ? 1 : 0.8);
        (_d = this.wait) === null || _d === void 0 ? void 0 : _d.position.set(this.center.x, this.info.y + this.info.height + (0, Global_1.px)(this.isLandscapeBox ? 35 : 45));
        (_e = this.footerText) === null || _e === void 0 ? void 0 : _e.position.set(this.center.x, this.app.screen.height - this.footerText.height + (0, Global_1.px)(15));
    }
}
exports.RoundScene = RoundScene;
